/**
 * AssessmentBillingField — the suggested-billing line as an EDITABLE
 * field (2026-07 redesign, founder ruling).
 *
 * Case notes are the ONLY road onto an invoice: this field never writes
 * a billable entry and never implies one. It is a suggestion the care
 * manager can adjust (hours + summary; direct edits win) that persists
 * with the saved draft, placed with the terminal actions. The drafter →
 * Kith assessment-door integration lands later; nothing here flows
 * through.
 *
 * Hours clamp on blur to the schema's 0.5–8.0 range (one decimal) so an
 * edit can never 400 the save.
 */

import { type ReactNode, useState } from 'react';
import type { AssessmentBillingSuggestion } from '@/lib/api';

export interface AssessmentBillingFieldProps {
  suggestion: AssessmentBillingSuggestion;
  onChange: (next: AssessmentBillingSuggestion) => void;
  disabled?: boolean;
}

const MIN_HOURS = 0.5;
const MAX_HOURS = 8;

function clampHours(raw: string, fallback: number): number {
  const n = Number.parseFloat(raw);
  if (!Number.isFinite(n)) return fallback;
  const clamped = Math.min(MAX_HOURS, Math.max(MIN_HOURS, n));
  return Math.round(clamped * 10) / 10;
}

export function AssessmentBillingField({
  suggestion,
  onChange,
  disabled = false,
}: AssessmentBillingFieldProps): ReactNode {
  // Local text state so partial input ("1.") doesn't fight the clamp;
  // committed (clamped) on blur.
  const [hoursText, setHoursText] = useState(suggestion.hours.toFixed(1));

  const commitHours = (): void => {
    const next = clampHours(hoursText, suggestion.hours);
    setHoursText(next.toFixed(1));
    if (next !== suggestion.hours) onChange({ ...suggestion, hours: next });
  };

  return (
    <section
      className="rounded-lg border-l-4 border-y border-r border-sage-800/[8%] p-4 space-y-2"
      style={{ backgroundColor: '#ECF1EE', borderLeftColor: 'var(--color-sage-400)' }}
      data-testid="assessment-billing-field"
      aria-label="Suggested billing"
    >
      <h3
        className="text-sm font-semibold flex items-center gap-1.5"
        style={{ color: 'var(--color-sage-800)' }}
      >
        <span aria-hidden="true">⏱️</span>
        <span>Suggested billing</span>
      </h3>
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor="assessment-billing-hours" className="sr-only">
          Suggested hours
        </label>
        <input
          id="assessment-billing-hours"
          type="number"
          inputMode="decimal"
          min={MIN_HOURS}
          max={MAX_HOURS}
          step={0.1}
          value={hoursText}
          onChange={(e) => setHoursText(e.target.value)}
          onBlur={commitHours}
          disabled={disabled}
          className="w-20 rounded-[10px] border border-sage-400/25 bg-paper px-2.5 py-1.5 text-sm font-semibold text-ink outline-none focus:border-sage-400 focus:ring-[3px] focus:ring-sage-800/[6%]"
          data-testid="assessment-billing-hours"
        />
        <span className="text-sm font-semibold" style={{ color: 'var(--color-ink)' }}>
          hours
        </span>
        <span className="text-ink-soft" aria-hidden="true">·</span>
        <span
          className="text-xs font-medium px-2 py-0.5 rounded-full border"
          style={{
            backgroundColor: 'var(--color-paper)',
            borderColor: '#C9D6CF',
            color: 'var(--color-sage-800)',
          }}
          data-testid="assessment-billing-category"
        >
          {suggestion.category}
        </span>
      </div>
      <div>
        <label htmlFor="assessment-billing-rationale" className="sr-only">
          Billing summary
        </label>
        <textarea
          id="assessment-billing-rationale"
          rows={2}
          value={suggestion.rationale}
          onChange={(e) => onChange({ ...suggestion, rationale: e.target.value })}
          disabled={disabled}
          className="block w-full rounded-[10px] border border-sage-400/25 bg-paper px-3 py-2 text-sm leading-relaxed text-ink outline-none focus:border-sage-400 focus:ring-[3px] focus:ring-sage-800/[6%]"
          data-testid="assessment-billing-rationale"
        />
      </div>
      <p className="text-xs text-ink-soft" data-testid="assessment-billing-no-write-note">
        A suggestion to carry into your case note. Nothing is billed from here; your case note is
        the only road onto an invoice.
      </p>
    </section>
  );
}
